// Third-party
import React, { ReactNode, Component } from 'react';
import classNames from 'classnames';
import uniqueId from 'lodash/uniqueId';

type Props = {
  className?: string;
  headerText?: string;
  headerContent?: ReactNode;
  headerRightContent?: ReactNode;
  children?: ReactNode;
  headerTag?: string;
  defaultOpen?: boolean;
  noTextHover?: boolean;
  icon?: string;
  tabIndex?: number;
  allowInteractiveHeader?: boolean;
  getToggleAriaLabel?: (isOpen: boolean) => string;
};

type State = {
  isOpen: boolean;
};

class CollapsibleContainer extends Component<Props, State> {
  uniqueId: string;

  // eslint-disable-next-line react/static-property-placement
  static defaultProps: Partial<Props> = {
    className: undefined,
    headerText: undefined,
    headerContent: undefined,
    headerRightContent: undefined,
    children: undefined,
    headerTag: 'div',
    defaultOpen: true,
    noTextHover: undefined,
    icon: undefined,
    tabIndex: undefined,
    allowInteractiveHeader: undefined,
    getToggleAriaLabel: undefined,
  };

  constructor(props: Props) {
    super(props);
    this.uniqueId = uniqueId('collapsible-container-');

    const { defaultOpen = true } = props;
    this.state = {
      isOpen: defaultOpen,
    };
  }

  handleClick = () => {
    this.setState(prevState => ({
      isOpen: !prevState.isOpen,
    }));
  };

  renderHeader = () => {
    const { headerText, headerContent, headerRightContent, icon, tabIndex, allowInteractiveHeader, getToggleAriaLabel, headerTag } = this.props;
    const HeaderTag = (headerTag as keyof JSX.IntrinsicElements) ?? 'div';
    const { isOpen } = this.state;

    const arrowClass = isOpen ? 'toggle-up' : 'toggle-down';
    const iconClass = `icon-${icon ?? 'cheveron-up'}`;

    if (allowInteractiveHeader) {
      const toggleAriaLabel = getToggleAriaLabel ? getToggleAriaLabel(isOpen) : headerText;

      const content = (
        <HeaderTag className="collapsible-container-header-content collapsible-container-header-content--interactive">
          <div className="collapsible-container-header-main">{headerContent ?? <span className="collapsible-container-header-text">{headerText}</span>}</div>

          {headerRightContent && <div className="collapsible-container-header-actions">{headerRightContent}</div>}

          <i className={classNames(iconClass, arrowClass)} />
        </HeaderTag>
      );

      return (
        <div className="collapsible-container-header-wrapper">
          <button
            id={`${this.uniqueId}-button`}
            type="button"
            onClick={this.handleClick}
            aria-controls={`${this.uniqueId}-content`}
            aria-expanded={isOpen}
            aria-label={toggleAriaLabel}
            className="collapsible-container-header collapsible-container-header-overlay"
            tabIndex={tabIndex}
          />
          {content}
        </div>
      );
    }

    const content = (
      <HeaderTag className="collapsible-container-header-content">
        <span>{headerText}</span>
        <i className={classNames(iconClass, arrowClass)} />
      </HeaderTag>
    );

    return (
      <button
        id={`${this.uniqueId}-button`}
        type="button"
        onClick={this.handleClick}
        aria-controls={`${this.uniqueId}-content`}
        aria-expanded={isOpen}
        className="collapsible-container-header"
        tabIndex={tabIndex}
      >
        {content}
      </button>
    );
  };

  render() {
    const { children, className, noTextHover } = this.props;
    const { isOpen } = this.state;
    const id = this.uniqueId;

    const classes = classNames('collapsible-container', { 'is-open': isOpen }, { 'no-text-hover': noTextHover }, className);

    return (
      <div className={classes}>
        {this.renderHeader()}

        <div id={`${id}-content`} className="collapsible-container-content" aria-labelledby={`${id}-button`} hidden={!isOpen}>
          {children}
        </div>
      </div>
    );
  }
}

export default CollapsibleContainer;
